import { RouteProp } from '@react-navigation/native';
import React, { useMemo } from 'react';
import { ProfilesTabParamList, useGoBack } from '../../../../../utils/appNavigationNames';
import useMedicalProfileData, {
    getAllResourceIdsFromGroupedResources,
} from '../../../../../hooks/api/useMedicalProfileData';
import { getIntl } from '../../../../../utils/intl';
import ResourceCard from '../../../../shared/ResourceCard';
import StaticSearchList from '../../../../shared/ResourceSearch/StaticSearchList';
import { pluralResourceCategoryMessages, resourceCategoryMessages } from '../../../../../utils/sharedMessages';
import { PrimaryRecordResourceCategory } from '../../../../../primary-record-types/MedicalDataTypes';
import {
    screenParamsToFilterValue,
    useApplySearchFilter,
} from '../../../../shared/ResourceSearch/ResourceListSearchFilters';
import { ActionButtonLayout } from '../../../../v2BaseComponents/ActionButtonLayout';
import { Action } from '../../../../v2BaseComponents/ActionButtonLayout/types';
import { StandardPrompt } from '../../../../v2BaseComponents/Prompt';
import Stack, { contentStackSpacing } from '../../../../v2BaseComponents/Stack';
import PillSelect, { PillOption } from '../../../../v2BaseComponents/Inputs/PillSelect';
import Text from '../../../../v2BaseComponents/Text';
import ProfileHeaderContainer from '../../../../v2BaseComponents/ProfileHeaderContainer';

const intl = getIntl('medical-data-bulk-update', {
    selected: '{count} Selected',
    selectAll: 'Select All',
    selectNone: 'Select None',
    delete: 'Delete',
    status: 'Status',
    cancel: 'Cancel',
    deleteConfirmationHeader: 'Delete {category}?',
    deleteConfirmationBody: 'Are you sure you want to delete {count} {category}?',
    deleteEHRDisclaimer:
        'Note that this change is only represented in Primary Record.  It is not updated in the EHR source system it may have come from.',
    active: 'Active',
    inactive: 'Inactive',
    chronic: 'Chronic',
    'ruled-out': 'Ruled Out',
    updateConfirmationHeader: 'Update {category}?',
    updateConfirmationBody: 'Are you sure you want to update the status for {count} {category} to {status}?',
    updateEHRDisclaimer:
        'Note that this change is only represented in Primary Record.  It is not updated in the EHR source system it may have come from.',
    update: 'Update',
});

const resourceStatusMap: Record<PrimaryRecordResourceCategory, string[]> = {
    allergy: ['active', 'inactive'],
    condition: ['active', 'inactive'],
    medication: ['active', 'inactive'],
    visit: [],
    vaccination: [],
    procedure: [],
    imaging: [],
    lab: [],
};

export type DataBulkUpdateOwnProps = {};

export type DataBulkUpdateProps = DataBulkUpdateOwnProps & {
    route: RouteProp<ProfilesTabParamList, 'data-bulk-update'>;
};
const DataBulkUpdate: React.FC<DataBulkUpdateProps> = (props) => {
    const [pendingUpdateStatus, setPendingUpdateStatus] = React.useState<string | null>(null);
    const [selectedResourceGroupIds, setSelectedResourceGroupIds] = React.useState<string[]>([]);
    const goBack = useGoBack();
    const { category, profileId } = props.route.params;
    const { groupedResources, isLoadingResources, bulkUpdateResources, isBulkUpdatingResources } =
        useMedicalProfileData({
            medicalProfileId: profileId,
            resourceCategory: category,
            onBulkUpdateSuccess: () => {
                goBack();
            },
        });
    const filterValue = useMemo(() => screenParamsToFilterValue(props.route.params), [props.route.params]);
    const filteredResources = useApplySearchFilter(groupedResources || [], filterValue, category);
    const statusOptions = useMemo(() => resourceStatusMap[category], [category]);
    const selectedResourceIds = useMemo(() => {
        const sourceFilteredResources = filteredResources.filter((r) => selectedResourceGroupIds.includes(r.id));
        return getAllResourceIdsFromGroupedResources(sourceFilteredResources);
    }, [filteredResources, selectedResourceGroupIds]);
    const [isDeletePromptOpen, setIsDeletePromptOpen] = React.useState(false);
    const actions: Action[] = [
        {
            title: intl('status'),
            icon: 'Edit2',
            isDisabled: selectedResourceGroupIds.length === 0 || isBulkUpdatingResources || isLoadingResources,
            onPress: () => setPendingUpdateStatus('active'),
            testID: 'bulk-update-status-prompt',
            show: statusOptions.length > 0,
        },
        {
            title: intl('delete'),
            icon: 'Trash2',
            isDisabled: selectedResourceGroupIds.length === 0 || isBulkUpdatingResources || isLoadingResources,
            onPress: () => setIsDeletePromptOpen(true),
            testID: 'bulk-update-delete-prompt',
            variant: 'alert',
        },
    ];

    const pillOptions = useMemo<PillOption[]>(
        () => [
            {
                label: intl('selectAll'),
                value: 'all',
                testID: 'bulk-update-select-all',
            },
            {
                label: intl('selectNone'),
                value: 'none',
                testID: 'bulk-update-select-none',
            },
        ],
        [],
    );
    const bulkSelectOptionValue =
        selectedResourceGroupIds.length === filteredResources.length
            ? 'all'
            : selectedResourceGroupIds.length === 0
            ? 'none'
            : null;

    return (
        <ProfileHeaderContainer profileId={profileId} bottomBorder omitChatButton>
            <ActionButtonLayout actions={actions}>
                <StaticSearchList
                    category={category}
                    resources={filteredResources || []}
                    isLoadingResources={isLoadingResources}
                    screenSearchParams={props.route.params}
                    style={{ flex: 1 }}
                    renderPreList={() => (
                        <Stack vertical fullWidth space={contentStackSpacing}>
                            <Stack horizontal fullWidth justify={'space-between'} align={'center'}>
                                <PillSelect
                                    value={bulkSelectOptionValue}
                                    onChange={(value) => {
                                        if (value === 'all') {
                                            setSelectedResourceGroupIds(filteredResources.map((r) => r.id));
                                        } else if (value === 'none') {
                                            setSelectedResourceGroupIds([]);
                                        }
                                    }}
                                    options={pillOptions}
                                />
                                <Text variant={'body-1'} testID="bulk-update-selected-message">
                                    {intl('selected', { count: selectedResourceGroupIds.length })}
                                </Text>
                            </Stack>
                        </Stack>
                    )}
                    renderFilteredResources={(resource) => (
                        <ResourceCard
                            isDisabled={isBulkUpdatingResources || isLoadingResources}
                            isSelectable
                            resource={resource}
                            category={category}
                            profileId={profileId}
                            onPress={() => {
                                if (selectedResourceGroupIds.includes(resource.id)) {
                                    setSelectedResourceGroupIds(
                                        selectedResourceGroupIds.filter((id) => id !== resource.id),
                                    );
                                } else {
                                    setSelectedResourceGroupIds([...selectedResourceGroupIds, resource.id]);
                                }
                            }}
                            isSelected={selectedResourceGroupIds.includes(resource.id)}
                        />
                    )}
                />
                <StandardPrompt
                    isOpen={isDeletePromptOpen}
                    setIsOpen={setIsDeletePromptOpen}
                    title={intl('deleteConfirmationHeader', {
                        category: pluralResourceCategoryMessages(category),
                    })}
                    innerTitle
                    actions={[
                        {
                            title: intl('delete'),
                            variant: 'alert',
                            onPress: () => {
                                setIsDeletePromptOpen(false);
                                bulkUpdateResources({
                                    action: 'delete',
                                    resourceCategory: category,
                                    resourceIds: selectedResourceIds,
                                });
                            },
                            testID: 'bulk-update-delete',
                        },
                        {
                            title: intl('cancel'),
                            variant: 'secondary',
                            onPress: () => setIsDeletePromptOpen(false),
                            testID: 'bulk-update-cancel',
                        },
                    ]}
                >
                    <Stack vertical fullWidth space={4} align="center">
                        <Text variant={'body-1'} alignText="center">
                            {intl('deleteConfirmationBody', {
                                count: selectedResourceGroupIds.length,
                                category: (selectedResourceGroupIds.length === 1
                                    ? resourceCategoryMessages(category)
                                    : pluralResourceCategoryMessages(category)
                                ).toLocaleLowerCase(),
                            })}
                        </Text>
                        <Text variant={'body-1'} alignText="center">
                            {intl('deleteEHRDisclaimer')}
                        </Text>
                    </Stack>
                </StandardPrompt>
                <StandardPrompt
                    isOpen={!!pendingUpdateStatus}
                    setIsOpen={(isOpen) => setPendingUpdateStatus(isOpen ? pendingUpdateStatus : null)}
                    title={intl('updateConfirmationHeader', {
                        category: pluralResourceCategoryMessages(category),
                    })}
                    innerTitle
                    actions={[
                        {
                            title: intl('update'),
                            icon: 'CheckCircle',
                            onPress: () => {
                                setPendingUpdateStatus(null);
                                // shouldn't happen, but keep ts happy

                                if (category === 'condition' || category === 'allergy') {
                                    bulkUpdateResources({
                                        action: 'update',
                                        resourceCategory: category,
                                        resourceIds: selectedResourceIds,
                                        status: pendingUpdateStatus as any,
                                    });
                                } else if (category === 'medication') {
                                    bulkUpdateResources({
                                        action: 'update',
                                        resourceCategory: category,
                                        resourceIds: selectedResourceIds,
                                        isActive: pendingUpdateStatus === 'active',
                                    });
                                }
                            },
                            testID: 'bulk-update-update',
                        },
                        {
                            title: intl('cancel'),
                            variant: 'secondary',
                            icon: 'XCircle',
                            onPress: () => setPendingUpdateStatus(null),
                            testID: 'bulk-update-cancel',
                        },
                    ]}
                >
                    <Stack vertical fullWidth space={4} align="center">
                        <Text variant={'body-1'} alignText="center">
                            {intl('updateConfirmationBody', {
                                count: selectedResourceGroupIds.length,
                                category: (selectedResourceGroupIds.length === 1
                                    ? resourceCategoryMessages(category)
                                    : pluralResourceCategoryMessages(category)
                                ).toLocaleLowerCase(),
                                status: intl(pendingUpdateStatus as any).toLocaleLowerCase(),
                            })}
                        </Text>
                        {statusOptions.length ? (
                            <PillSelect
                                value={pendingUpdateStatus}
                                onChange={(value) => {
                                    setPendingUpdateStatus(value);
                                }}
                                options={statusOptions.map((_) => ({ label: intl(_ as any), value: _ }))}
                            />
                        ) : null}
                        <Text variant={'body-1'} alignText="center">
                            {intl('updateEHRDisclaimer')}
                        </Text>
                    </Stack>
                </StandardPrompt>
            </ActionButtonLayout>
        </ProfileHeaderContainer>
    );
};

export default React.memo(DataBulkUpdate);
